import { getCookie } from "@insite/client-framework/Common/Cookies";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { isOutOfStock } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import addToWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import removeCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/RemoveCartLine";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import translate from "@insite/client-framework/Translate";
import { ProductSettingsModel, ProductSubscriptionDto, PromotionModel } from "@insite/client-framework/Types/ApiModels";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import ProductBinNumbers from "@insite/content-library/Components/ProductBinNumbers";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import CartLineNotes, { CartLineNotesStyles } from "@insite/content-library/Widgets/Cart/CartLineNotes";
import CartLineQuantity, { CartLineQuantityStyles } from "@insite/content-library/Widgets/Cart/CartLineQuantity";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import DatePicker, { DatePickerPresentationProps, DatePickerState } from "@insite/mobius/DatePicker";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconMemo, IconPresentationProps } from "@insite/mobius/Icon";
import XCircle from "@insite/mobius/Icons/XCircle";
import LazyImage from "@insite/mobius/LazyImage";
// import LazyImage from "@insite/mobius/LazyImage";
import ProductAddedToCartMessage from "@insite/content-library/Components/ProductAddedToCartMessage";
import Trash from "@insite/mobius/Icons/Trash";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { comingSoonDiscontinueDates } from "@tKgpco/Common/commonFunctions";
import SeeMoreInfoModal from "@tKgpco/Components/SeeMoreInfoModal";
import { getProductsWithInvalidPrice } from "@tKgpco/Handlers/Cart/CartSelectorCustom";
import ProductAvailability, { ProductAvailabilityStyles } from "@tKgpco/Overrides/Components/ProductAvailability";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@tKgpco/Overrides/Components/ProductPartNumbers";
import { ErrorContext } from "@tKgpco/Overrides/Pages/CartPage";
import { productListLineStyles } from "@tKgpco/Overrides/Widgets/MyListsDetails/MyListsDetailsProductListLine";
import CustomApplicationState from "@tKgpco/Store/CustomApplicationState";
import setReelQtyError from "@tKgpco/Store/Data/Cart/Handlers/setReelQtyErrorHandler";
import setMinimumQtyError from "@tKgpco/Store/Data/ProductMultipleQty/Handlers/setProductMinimunQtyErrorHandler";
import setMultipleQtyError from "@tKgpco/Store/Data/ProductMultipleQty/Handlers/setProductMultpleSalesQtyErrorHandler";
import { COLORS, TYPOGRAPHY } from "@tKgpco/theme/design";
import React, { FC, useContext } from "react";
import { connect, HandleThunkActionCreator, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    cart: Cart;
    promotions: PromotionModel[];
    productSettings: ProductSettingsModel;
    showInventoryAvailability?: boolean;
    showLineNotes?: boolean;
    showImage?: boolean;
    showProductTitle?: boolean;
    showBrand?: boolean;
    showPerUnitPrice?: boolean;
    showPriceLabel?: boolean;
    showAddToList?: boolean;
    showProductNumber?: boolean;
    updateCartLine: HandleThunkActionCreator<typeof updateCartLine>;
    removeCartLine: HandleThunkActionCreator<typeof removeCartLine>;
    showRemoveAction?: boolean;
    hideAddToList?: boolean;
    extendedStyles?: CartLineCardExpandedStyles;
    showVmiBinDetails?: boolean;
}

const mapStateToProps = (state: CustomApplicationState) => ({
    wishListSettings: getSettingsCollection(state).wishListSettings,
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
    showPricing: state.context.showPricing,
    properties: state.context.settings.properties,
    isAuthenticated: state.context.session?.isAuthenticated && !state.context.session?.isGuest,
    addToCartPopupTimeout: getSettingsCollection(state).cartSettings.addToCartPopupTimeout,
    hasMinimumQtyError: state.KGPState.ProductMultipleQtyStore.showMinimumQtyError,
    hasMultipleSalesQtyError: state.KGPState.ProductMultipleQtyStore.showMultipleQtyError,
    applyHeaderDatetoAllLines: state.KGPState.ApplyDateToAllLineStore.applyDateToAllLines,
});

const mapDispatchToProps = {
    setAddToListModalIsOpen,
    addToWishList,
    setMinimumQtyError,
    setMultipleQtyError,
    setReelQtyError,
};

type Props = OwnProps &
    HasCartLineContext &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

export interface CartLineCardExpandedStyles {
    container?: GridContainerProps;
    productImageGridItem?: GridItemProps;
    productImage?: ProductImageStyles;
    cartLineInfoGridItem?: GridItemProps;
    cartLineInfoContainer?: GridContainerProps;
    productInfoGridItem?: GridItemProps;
    productInfoContainer?: GridContainerProps;
    productBrandAndDescriptionGridItem?: GridItemProps;
    productBrand?: ProductBrandStyles;
    productDescriptionGridItem?: GridItemProps;
    productDescription?: ProductDescriptionStyles;
    configurationGridItem?: GridItemProps;
    configurationOptionText?: TypographyProps;
    productPartNumbersGridItem?: GridItemProps;
    productPartNumbers?: ProductPartNumbersStyles;
    quantityAndExtendedUnitNetPriceGridItem?: GridItemProps;
    quantityAndExtendedUnitNetPriceContainer?: GridContainerProps;
    quantityGridItem?: GridItemProps;
    quantity?: CartLineQuantityStyles;
    extendedUnitNetPriceGridItem?: GridItemProps;
    extendedUnitNetPriceText?: TypographyProps;
    vatLabelText?: TypographyPresentationProps;
    subtotalWithoutVatText?: TypographyPresentationProps;
    cartLineErrorMessageGridItem?: GridItemProps;
    cartLineErrorMessageText?: TypographyProps;
    productPriceAndAvailabilityGridItem?: GridItemProps;
    productPrice?: ProductPriceStyles;
    promotionNameText?: TypographyProps;
    productAvailability?: ProductAvailabilityStyles;
    addToListLink?: LinkPresentationProps;
    deliveryScheduleButton?: ButtonPresentationProps;
    costCodeGridItem?: GridItemProps;
    costCodeLabelText?: TypographyPresentationProps;
    costCodeSelect?: SelectPresentationProps;
    costCodeText?: TypographyPresentationProps;
    cartLineNotesGridItem?: GridItemProps;
    cartLineNotes?: CartLineNotesStyles;
    removeCartLineGridItem?: GridItemProps;
    removeCartLineIcon?: IconPresentationProps;
    listRemoveCart?: InjectableCss;
    removeItem?: InjectableCss;
    fromDate?: DatePickerPresentationProps;
    earlistShipDateLabel?: InjectableCss;
    earlistShipDate?: InjectableCss;
    esdWrapper?: InjectableCss;
    cartMsg?: InjectableCss;
    dateWrapper?: InjectableCss;
    binInformationsGridItem?: GridItemProps;
    productBinNumbers?: ProductPartNumbersStyles;
    badgeImgWrapper?: InjectableCss;
    productImageWrapper?: InjectableCss;
    seeLeadTimeLink?: LinkPresentationProps;
    seeLeadTimeLinkGridItem?: GridItemProps;
    tooltipText?: InjectableCss;
    itemNotFound?: InjectableCss;
    notFoundLabel?: TypographyPresentationProps;
    errMSg?: InjectableCss;
}

export const cartLineCardExpandedStyles: CartLineCardExpandedStyles = {
    container: {
        gap: 0,
        css: css`
            border: 1px solid #e1e7ed;
            border-radius: 8px;
            padding: 1rem 15px 2.5rem;
            margin-bottom: 1rem;

            &.invalid-product-grid {
                background-color: #fff3cd;
                border-color: #e23939;
            }
        `,
    },
    productImageGridItem: {
        width: [4, 4, 4, 2, 2],
        css: css`
            padding: 0 10px 10px 0;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: 10px;
                    `,
                    css`
                        font-size: 10px;
                    `,
                    css`
                        font-size: 10px;
                    `,
                    css`
                        font-size: 10px;
                        width: 160px;
                    `,
                    css`
                        width: 160px;
                    `,
                ])}
        `,
    },
    cartLineInfoGridItem: { width: [8, 8, 8, 10, 10] },
    cartLineInfoContainer: { gap: 20 },
    productInfoGridItem: { width: [12, 12, 12, 8, 8] },
    productInfoContainer: { gap: 12 },
    productBrandAndDescriptionGridItem: {
        css: css`
            flex-direction: column;
        `,
        width: 12,
    },
    productDescriptionGridItem: {
        width: 12,
    },
    configurationGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    productPartNumbersGridItem: { width: 12 },
    quantityAndExtendedUnitNetPriceGridItem: { width: 12 },
    quantityGridItem: { width: 6 },
    extendedUnitNetPriceGridItem: {
        width: 12,
        css: css`
            align-items: flex-end;
        `,
    },
    extendedUnitNetPriceText: {
        weight: "bold",
        css: css`
            margin-top: 10px;
            font-size: 20px;
            @media print {
                font-size: 14px;
                margin-left: 10px;
            }
        `,
    },
    cartLineErrorMessageGridItem: { width: 12 },
    cartLineErrorMessageText: { color: "danger" },
    productPriceAndAvailabilityGridItem: {
        width: [12, 8, 12, 4, 4],
        css: css`
            position: relative;
            flex-direction: column;
            // span[data-test-selector^="productPrice_unitNetPrice"] {
            //     color: rgb(30, 117, 186);
            //     display: inline-block;
            //     margin-bottom: 0.5em;
            // }
        `,
    },
    addToListLink: {
        css: css`
            margin-top: 0px;
            font-size: 13px;
            font-weight: 400;
            &:hover {
                text-decoration: underline;
                text-decoration-color: ${COLORS.primary.base};
            }
        `,
        typographyProps: {
            color: `${COLORS.black} !important`,
            css: css`
                &:hover {
                    color: ${getColor("primary")} !important;
                }
            `,
        },
    },
    costCodeGridItem: {
        width: [12, 12, 12, 6, 6],
        css: css`
            flex-direction: column;
        `,
    },
    costCodeLabelText: {
        css: css`
            margin-bottom: 10px;
        `,
    },
    cartLineNotesGridItem: {
        width: 12,
        css: css`
            width: 100%;
            // position: relative;
            // bottom: 20px;

            button {
                position: absolute;
            }
        `,
    },
    removeCartLineGridItem: {
        css: css`
            justify-content: center;
        `,
        width: [2, 2, 2, 1, 1],
    },
    removeCartLineIcon: { src: XCircle },
    productPrice: {
        price: {
            unitOfMeasureText: {
                css: css`
                    white-space: nowrap;
                    font-size: 14px;
                    font-weight: 500;
                    color: ${getColor("primary")};
                `,
            },
            priceText: {
                css: css`
                    font-weight: 500;
                    font-size: 14px;
                    color: ${getColor("primary")};
                `,
            },
        },
        priceWrapper: {
            css: css`
                margin-bottom: 15px;
            `,
        },
    },
    listRemoveCart: {
        css: css`
            display: flex;
            justify-content: space-between;
            width: 100%;
            align-items: center;
            margin-top: 15px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            margin-right: 0px !important;
                        `,
                        css`
                            margin-right: 0px !important;
                        `,
                        css`
                            position: absolute;
                            bottom: -13px;
                            left: -5px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    productPartNumbers: {
        erpNumberGridItem: {
            css: css`
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                flex-basis: 50% !important;
                            `,
                        ],
                        "min",
                    )}
            `,
        },
        erpNumberLabelText: {
            size: 13,
            css: css`
                margin-right: 5px;
            `,
        },
        erpNumberValueText: {
            size: 13,
        },
        customerNameLabelText: {
            size: 13,
        },
        customerNameValueText: {
            size: 13,
        },
        manufacturerItemLabelText: {
            size: 13,
        },
        manufacturerItemValueText: {
            size: 13,
        },
        customerNameGridItem: {
            css: css`
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                flex-basis: 50% !important;
                            `,
                        ],
                        "min",
                    )}
            `,
        },
        manufacturerItemGridItem: {
            css: css`
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                flex-basis: 50% !important;
                            `,
                        ],
                        "min",
                    )}
            `,
        },
    },
    removeItem: {
        css: css`
            font-size: 13px;
            font-weight: 400;
            color: ${COLORS.black};
            margin-left: auto;
            &:hover {
                color: #fc3939;
                text-decoration: underline;
            }

            &.invalid {
                color: #fff;
                font-weight: bold;
                padding: 10px 20px;
                border-radius: 4px;
                background-color: #e23939;
                span {
                    margin-right: 8px;

                    svg {
                        stroke: #fff;
                    }
                }
            }
        `,
    },
    cartLineNotes: {
        editableNotesTextArea: {
            css: css`
                background: #f1f6fb !important;
            `,
        },
        toggleLink: {
            css: css`
                margin-bottom: 10px;
                font-weight: 400;
                font-size: 14px;
                &:hover {
                    background: #e1e7ed;
                }
            `,
            typographyProps: {
                color: `${COLORS.black} !important`,
            },
        },
    },
    productAvailability: {
        messageGridItem: {
            css: css`
                flex-basis: auto;
                align-items: center;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                margin-right: 10px !important;
                            `,
                            css`
                                margin-right: 15px !important;
                            `,
                        ],
                        "min",
                    )}
            `,
        },
        messageText: {
            css: css`
                font-size: 13px !important;
            `,
        },
    },
    productBrand: {
        nameLink: {
            typographyProps: {
                css: css`
                    font-size: 13px !important;
                `,
            },
        },
    },
    productDescription: {
        productDetailLink: {
            typographyProps: {
                size: 15,
            },
        },
    },
    fromDate: {
        cssOverrides: {
            formField: css`
                width: 100%;
                padding: 5px;
                label {
                    font-size: 13px;
                    color: #1a1a1a;
                }
            `,
            formInputWrapper: css`
                margin-top: 0px !important;
            `,
            inputSelect: css`
                font-size: 13px !important;
                border-radius: 8px;
                color: ${COLORS.primary.light};
                & :focus {
                    border-radius: 8px;
                }
                button {
                    span {
                        color: ${COLORS.primary.light};
                    }
                }
            `,
            datePicker: css`
                div[class^="react-calendar"] {
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(
                            theme,
                            [
                                css`
                                    right: -60px !important;
                                `,
                            ],
                            "max",
                        )}
                }
                > div[class^="react-datetime-picker"] {
                    @media print {
                        border: none !important;
                    }
                    > div > div[class="react-datetime-picker__inputGroup"] {
                        pointer-events: none;
                        @media print {
                            padding-left: 0px !important;
                        }
                        > span[class^="react-datetime-picker__inputGroup__leadingZero"] {
                            padding: 3px 0px 3px 3px;
                        }
                    }
                }
            `,
        },
    },
    esdWrapper: {
        css: css`
            @media print {
                margin-left: 0px !important;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 5px;
                    `,
                    css`
                        margin-left: 31px;
                    `,
                    css`
                        margin-left: 31px;
                    `,
                    css`
                        margin-left: 31px;
                    `,
                    css`
                        margin-left: 31px;
                    `,
                ])}
        `,
    },
    earlistShipDateLabel: {
        css: css`
            font-size: 13px;
            font-weight: 600;
            color: #1a1a1a;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin: 0;
                    `,
                    css`
                        margin: 5px;
                    `,
                    css`
                        margin: 5px 0;
                    `,
                    css`
                        margin: 5px 0;
                    `,
                    css`
                        margin: 5px 0;
                    `,
                ])}
        `,
    },
    earlistShipDate: {
        css: css`
            font-size: 13px;
            margin: 10px 0;
            color: #1a1a1a;
            @media print {
                margin-left: 5px !important;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            margin: 5px 0;
                        `,
                        css`
                            margin: 10px 0;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    cartMsg: {
        css: css`
            width: 100%;
            font-size: 12px;
            color: #fc3939;
            margin-top: 5px;
            padding: 0 5px;
        `,
    },
    dateWrapper: {
        css: css`
            position: relative;

            .react-datetime-picker--disabled {
                background: rgb(233, 233, 233) !important;
                pointer-events: none;
            }
            .dateErrorStyle {
                .react-datetime-picker {
                    border: 2px solid ${COLORS.error};
                }
            }
        `,
    },
    quantity: {
        editableQuantityTextField: {
            css: css`
                @media print {
                    margin-right: 10px;
                }
            `,
        },
    },
    binInformationsGridItem: {
        width: 12,
    },
    badgeImgWrapper: {
        css: css`
            position: absolute;
            left: 50%;
            top: 50%;
            width: 65%;
            height: 65%;
            transform: translate(-50%, -50%);
            pointer-events: none;
        `,
    },
    productImageWrapper: {
        css: css`
            position: relative;
        `,
    },
    seeLeadTimeLink: {
        css: css`
            & :hover .tooltipText {
                visibility: visible;
            }
        `,
    },
    seeLeadTimeLinkGridItem: {
        width: 5,
        css: css`
            align-items: center;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        justify-content: flex-end;
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
            > span {
                background: #e1e7ed;
                padding: 3px 4px;
                border-radius: 4px;
                margin: 0 auto;
                > span {
                    color: #353432 !important;
                    font-size: ${TYPOGRAPHY.size.vTiny};
                    @media (max-width: 1199px) and (min-width: 992px) {
                        font-size: 10px !important;
                    }
                }
            }
        `,
    },
    tooltipText: {
        css: css`
            visibility: hidden;
            width: auto;
            background-color: black;
            color: #fff;
            text-align: center;
            border-radius: 6px;
            padding: 5px 6px;
            opacity: 0.8;
            position: absolute;
            z-index: 1;
            margin-top: 10px;
        `,
    },
    itemNotFound: {
        css: css`
            font-size: 14px;
            margin: 12px 0px;
            span {
                padding-right: 5px;
            }
        `,
    },
    notFoundLabel: {
        size: 14,
        weight: 700,
    },
    errMSg: {
        css: css`
            font-size: 15px;
            font-weight: bold;
            color: #fc3939;
        `,
    },
};

const CartLineCardExpanded: FC<Props> = ({
    cart,
    cartLine,
    promotions,
    productSettings,
    showInventoryAvailability,
    showLineNotes,
    showImage = true,
    showProductTitle = true,
    showBrand = true,
    showPerUnitPrice = true,
    showPriceLabel = true,
    showAddToList = true,
    showProductNumber = true,
    updateCartLine,
    removeCartLine,
    extendedStyles,
    showRemoveAction,
    wishListSettings,
    setAddToListModalIsOpen,
    addToWishList,
    hideAddToList,
    enableVat,
    vatPriceDisplay,
    showPricing,
    showVmiBinDetails,
    properties,
    isAuthenticated,
    setMinimumQtyError,
    setMultipleQtyError,
    hasMinimumQtyError,
    hasMultipleSalesQtyError,
    addToCartPopupTimeout,
    setReelQtyError,
    applyHeaderDatetoAllLines,
}) => {
    const toasterContext = useContext(ToasterContext);
    const currentMode = getCookie(NavigationModeCookieName);
    const { comingSoonImagePath, discontinuedImagePath } = properties;
    const { state, updateError } = useContext(ErrorContext);
    const cartID = cartLine?.id || "";
    const requiredDateErrors: string[] = state?.requiredDateErrors || [];
    const minimumOrderQty = Number(cartLine.properties.minimumOrderQty) || 0;
    const multipleSaleQty = Number(cartLine.properties.multipleSaleQty) || 0;
    const maxReelQty = Number(cartLine.properties.reelTotalQty) || 0;

    const dateError = requiredDateErrors.includes(cartID) ? `dateErrorStyle` : "";

    const qtyOrderedChangeHandler = (qtyOrdered: number) => {
        if (minimumOrderQty > 1 && qtyOrdered < minimumOrderQty) {
            setMinimumQtyError({
                setMinimumQtyError: true,
            });
            return;
        }

        if (!(qtyOrdered <= minimumOrderQty) && multipleSaleQty > 1 && qtyOrdered % multipleSaleQty !== 0) {
            setMultipleQtyError({
                setMultipleQtyError: true,
            });
            return;
        }

        const totalReelQty = cart.cartLines
            ?.filter(cartItem => cartItem.properties.reelId === cartLine.properties.reelId)
            .reduce((acc, cartItem) => {
                acc += cartLine.id === cartItem.id ? qtyOrdered : cartItem?.qtyOrdered || 0;
                return acc;
            }, 0);

        if (maxReelQty && totalReelQty && totalReelQty > maxReelQty) {
            setReelQtyError({
                setReelQtyError: true,
            });
            toasterContext.addToast({
                body: `${siteMessage("qty_excedded")}`,
                messageType: "danger",
                timeoutLength: addToCartPopupTimeout,
            });
            return;
        }

        if (qtyOrdered !== cartLine.qtyOrdered) {
            if (qtyOrdered <= 0) {
                removeCartLine({ cartLineId: cartLine.id });
            } else {
                updateCartLine({
                    cartLine: {
                        ...cartLine,
                        qtyOrdered,
                    },
                    onComplete(result: any) {
                        const cartline = result?.apiResult?.result;
                        if (cartline?.isQtyAdjusted) {
                            toasterContext.addToast({
                                body: (
                                    <ProductAddedToCartMessage
                                        isQtyAdjusted={cartline.isQtyAdjusted}
                                        multipleProducts={false}
                                    />
                                ),
                                messageType: "success",
                                timeoutLength: cartline.isQtyAdjusted
                                    ? 1000 * 60 * 60 * 24 * 365
                                    : addToCartPopupTimeout,
                            });
                        }
                    },
                });
            }
        }
    };

    const costCodeChangeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const costCode = event.currentTarget.value;
        if (costCode !== cartLine.costCode) {
            updateCartLine({
                cartLine: {
                    ...cartLine,
                    costCode,
                },
                shouldNotLoadFullCart: true,
            });
        }
    };

    // requiredDate
    const tzOffset = new Date().getTimezoneOffset() * 60000;
    const requiredDateHandler = (value: DatePickerState) => {
        const requiredDate1 = value.selectedDay;
        let requiredDate = requiredDate1
            ? new Date(requiredDate1.getTime() - tzOffset).toISOString().split("T")[0]
            : "";
        const splitArr = requiredDate.split("-");
        requiredDate = `${splitArr[1]}/${splitArr[2]}/${splitArr[0]}`;
        if (requiredDate1 && requiredDate && requiredDate !== cartLine.properties.requiredDate) {
            const removeSelected = requiredDateErrors.filter(el => {
                return cartLine.id !== el;
            });

            updateError("requiredDateErrors", removeSelected);

            updateCartLine({
                cartLine: {
                    ...cartLine,
                    properties: {
                        ...cartLine.properties,
                        requiredDate,
                    },
                },
            });
        }
    };

    const notesChangeHandler = (notes: string) => {
        if (notes !== cartLine.notes) {
            updateCartLine({
                cartLine: {
                    ...cartLine,
                    notes,
                },
                shouldNotReloadPromotions: true,
                onSuccess: () => {
                    toasterContext.addToast({
                        body: translate("Line Notes Updated"),
                        messageType: "success",
                    });
                },
                onComplete(resultProps) {
                    if (resultProps.apiResult) {
                        // "this" is targeting the object being created, not the parent SFC
                        // eslint-disable-next-line react/no-this-in-sfc
                        this.onSuccess?.();
                    }
                },
            });
        }
    };

    const removeCartLineClickHandler = () => {
        if (!showRemoveAction) {
            return;
        }

        removeCartLine({ cartLineId: cartLine.id });
    };

    const addToListClickHandler = () => {
        if (!wishListSettings) {
            return;
        }

        const productInfo = {
            productId: cartLine.productId!,
            qtyOrdered: cartLine.qtyOrdered!,
            unitOfMeasure: cartLine.unitOfMeasure,
        };
        if (!wishListSettings.allowMultipleWishLists) {
            addToWishList({
                productInfos: [productInfo],
                onSuccess: () => {
                    toasterContext.addToast({ body: siteMessage("Lists_ProductAdded"), messageType: "success" });
                },
                onComplete(resultProps) {
                    if (resultProps.result?.wishList) {
                        // "this" is targeting the object being created, not the parent SFC
                        // eslint-disable-next-line react/no-this-in-sfc
                        this.onSuccess?.(resultProps.result.wishList);
                    } else if (resultProps.result?.errorMessage) {
                        toasterContext.addToast({ body: resultProps.result.errorMessage, messageType: "danger" });
                    }
                },
            });
            return;
        }

        setAddToListModalIsOpen({ modalIsOpen: true, productInfos: [productInfo] });
    };

    const saveScheduleHandler = (updatedSubscription: ProductSubscriptionDto) => {
        updateCartLine({
            cartLine: {
                ...cartLine,
                properties: {
                    productSubscription: JSON.stringify(updatedSubscription),
                },
            },
        });
    };

    const sumQtyPerUom = cart.cartLines!.reduce((sum, current) => {
        return current.productId === cartLine.productId
            ? sum + current.qtyPerBaseUnitOfMeasure * current.qtyOrdered!
            : sum;
    }, 0);

    const errorMessages: React.ReactNode[] = [];
    if (showInventoryAvailability && cartLine.hasInsufficientInventory && !isOutOfStock(cartLine)) {
        const tooManyRequestedMessage = siteMessage(
            "Cart_ToManyQtyRequested",
            cartLine.qtyOnHand.toLocaleString(),
            sumQtyPerUom.toLocaleString(),
        );
        errorMessages.push(tooManyRequestedMessage);
    }

    if (cartLine.isRestricted) {
        errorMessages.push(translate("Restricted product"));
    }

    if (!cartLine.isActive) {
        errorMessages.push(translate("Inactive Product"));
    }

    if (cartLine.cantBuy) {
        errorMessages.push(translate("View-only product"));
    }

    const [styles] = React.useState(() => mergeToNew(cartLineCardExpandedStyles, extendedStyles));

    let showLeadTime = cartLine.properties?.isDropShip
        ? cartLine.properties?.isDropShip?.toLowerCase() === "true"
        : false;
    if (
        cartLine.properties?.isDropShip &&
        cartLine.properties?.isDropShip?.toLowerCase() === "false" &&
        cartLine.qtyOnHand < cartLine.qtyOrdered! &&
        cartLine.properties?.showContactUsMessage !== "true"
    ) {
        showLeadTime = true;
    }
    let leadtime = "";
    if (window.localStorage.getItem("customInventory")) {
        const inventory = JSON.parse(window.localStorage.getItem("customInventory") || "null");
        if (inventory && inventory.length > 0) {
            const filterInventory = inventory.filter((data: any) => data.productId === cartLine.productId);
            if (filterInventory.length > 0) {
                leadtime = filterInventory[0].additionalResults?.leadTime;
            }
        }
    }
    const { isComingSoon, isDiscontinued } = comingSoonDiscontinueDates(cartLine);
    let showPriceMessage = true;
    if (cartLine.properties?.showContactUsMessage?.toLowerCase() === "true") {
        showPriceMessage = false;
    }

    const invalidProductsId = getProductsWithInvalidPrice(cart)?.map(cartLine => cartLine.id);

    return (
        <>
            <GridContainer
                {...styles.container}
                className={invalidProductsId?.includes(cartLine.id) ? "invalid-product-grid" : "valid-product-grid"}
                data-test-selector={`cartline_expanded_${cartLine.productId}_${cartLine.unitOfMeasure}`}
            >
                <GridItem {...styles.productImageGridItem}>
                    <StyledWrapper {...styles.productImageWrapper}>
                        {showImage && <ProductImage product={cartLine} extendedStyles={styles.productImage} />}
                        {isAuthenticated && isComingSoon && (
                            <StyledWrapper className="coming-soon" {...styles.badgeImgWrapper}>
                                <LazyImage src={comingSoonImagePath} />
                            </StyledWrapper>
                        )}
                        {isAuthenticated && isDiscontinued && (
                            <StyledWrapper className="discontinued" {...styles.badgeImgWrapper}>
                                <LazyImage src={discontinuedImagePath} />
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                </GridItem>
                <GridItem {...styles.cartLineInfoGridItem}>
                    <GridContainer {...styles.cartLineInfoContainer}>
                        <GridItem {...styles.productInfoGridItem}>
                            <GridContainer {...styles.productInfoContainer}>
                                <GridItem {...styles.productBrandAndDescriptionGridItem}>
                                    {showBrand && cartLine.brand && (
                                        <ProductBrand brand={cartLine.brand} extendedStyles={styles.productBrand} />
                                    )}
                                    {showProductTitle && (
                                        <ProductDescription
                                            product={cartLine}
                                            extendedStyles={styles.productDescription}
                                        />
                                    )}
                                </GridItem>
                                {!cartLine.isFixedConfiguration && cartLine.sectionOptions!.length > 0 && (
                                    <GridItem {...styles.configurationGridItem}>
                                        {cartLine.sectionOptions!.map(option => (
                                            <Typography
                                                {...styles.configurationOptionText}
                                                key={option.sectionOptionId}
                                            >
                                                {`${option.sectionName}:${option.optionName}`}
                                            </Typography>
                                        ))}
                                    </GridItem>
                                )}
                                <GridItem {...styles.productPartNumbersGridItem}>
                                    <ProductPartNumbers
                                        productNumber={cartLine.erpNumber}
                                        customerProductNumber={cartLine.customerName}
                                        manufacturerItem={cartLine.manufacturerItem}
                                        extendedStyles={styles.productPartNumbers}
                                        showProductNumber={showProductNumber}
                                        product={cartLine}
                                        productReelId={cartLine?.properties?.reelId}
                                        productSiteId={cartLine?.properties?.siteId}
                                        //  extendedStyles={productListLineStyles.productPartNumbersStyles}
                                    />
                                </GridItem>
                                {showVmiBinDetails && currentMode === "Vmi" && (
                                    <GridItem {...styles.binInformationsGridItem}>
                                        <ProductBinNumbers
                                            locationId={cart.vmiLocationId}
                                            binId={cartLine.vmiBinId}
                                            isCondensed={false}
                                            extendedStyles={styles.productBinNumbers}
                                        />
                                    </GridItem>
                                )}
                                <GridItem width={[12, 12, 12, 12, 12]}>
                                    {showInventoryAvailability && !cartLine.quoteRequired && (
                                        <ProductAvailability
                                            productId={cartLine.productId!}
                                            availability={cartLine.availability!}
                                            unitOfMeasure={cartLine.unitOfMeasure}
                                            trackInventory={cartLine.trackInventory}
                                            extendedStyles={styles.productAvailability}
                                            inventory={cartLine.qtyOnHand}
                                        />
                                    )}
                                </GridItem>
                                <GridItem width={[12, 12, 12, 12, 12]}>
                                    {isAuthenticated && <SeeMoreInfoModal product={cartLine} />}
                                </GridItem>
                                {/* custom propperty start */}
                                <StyledWrapper {...styles.dateWrapper}>
                                    <StyledWrapper className={dateError}>
                                        <DatePicker
                                            {...styles.fromDate}
                                            label={translate("Requested Delivery Date*")}
                                            selectedDay={
                                                cartLine.properties?.requiredDate
                                                    ? new Date(cartLine.properties?.requiredDate)
                                                    : undefined
                                            }
                                            // selectedDay={fromDate}
                                            onDayChange={requiredDateHandler}
                                            dateTimePickerProps={{
                                                clearIcon: null,
                                                minDate: new Date(),
                                            }}
                                            error=""
                                        />
                                    </StyledWrapper>
                                </StyledWrapper>
                                {/* <StyledWrapper {...styles.esdWrapper}>
                                    <Typography as="p" {...styles.earlistShipDateLabel}>
                                        {translate("Earliest Ship Date")}
                                    </Typography>
                                    <Typography as="p" {...styles.earlistShipDate}>
                                        {cartLine.properties.earliestShipDate}
                                    </Typography>
                                </StyledWrapper> */}
                                {isAuthenticated && showLeadTime && leadtime && leadtime !== "" && (
                                    <GridItem {...styles.seeLeadTimeLinkGridItem}>
                                        <Link {...styles.seeLeadTimeLink} data-test-selector="seeLeadTimeLink">
                                            {translate("See Lead Time")}
                                            <StyledWrapper
                                                className="tooltipText"
                                                {...styles.tooltipText}
                                            >{`${leadtime} days`}</StyledWrapper>
                                        </Link>
                                    </GridItem>
                                )}
                                {cartLine.properties.productMessage && (
                                    <Typography as="p" {...styles.cartMsg}>
                                        {/* {siteMessage(
                                        "This item can only be shipped “Best Way Surface.“ These are typically hazardous items",
                                        )} */}
                                        {cartLine.properties.productMessage}
                                    </Typography>
                                )}
                                {cartLine.properties.isProductNonReturnable && (
                                    <Typography as="p" {...styles.cartMsg}>
                                        {siteMessage("This item cannot be returned.")}
                                    </Typography>
                                )}
                                {/* custom propperty end */}
                                {/* <GridItem {...styles.quantityAndExtendedUnitNetPriceGridItem}>
                                <GridContainer {...styles.quantityAndExtendedUnitNetPriceContainer}>
                                    <GridItem {...styles.quantityGridItem}>
                                        <CartLineQuantity
                                            cart={cart}
                                            editable={true}
                                            onQtyOrderedChange={qtyOrderedChangeHandler}
                                            extendedStyles={styles.quantity}
                                        />
                                    </GridItem>
                                    {!cartLine.quoteRequired && !cart.cartNotPriced && (
                                        <GridItem {...styles.extendedUnitNetPriceGridItem}>
                                            <Typography
                                                {...styles.extendedUnitNetPriceText}
                                                data-test-selector="cartline_extendedUnitNetPrice"
                                            >
                                                {enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                                                    ? cartLine.pricing.extendedUnitRegularPriceWithVatDisplay
                                                    : cartLine.pricing.extendedUnitNetPriceDisplay}
                                            </Typography>
                                            {enableVat && (
                                                <>
                                                    <Typography as="p" {...styles.vatLabelText}>
                                                        {vatPriceDisplay === "DisplayWithVat" ||
                                                        vatPriceDisplay === "DisplayWithAndWithoutVat"
                                                            ? `${translate("Inc. VAT")} (${cartLine.pricing.vatRate}%)`
                                                            : translate("Ex. VAT")}
                                                    </Typography>
                                                    {vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                                                        <>
                                                            <Typography {...styles.subtotalWithoutVatText}>
                                                                {cartLine.pricing.extendedUnitNetPriceDisplay}
                                                            </Typography>
                                                            <Typography as="p" {...styles.vatLabelText}>
                                                                {translate("Ex. VAT")}
                                                            </Typography>
                                                        </>
                                                    )}
                                                </>
                                            )}
                                        </GridItem>
                                    )}
                                </GridContainer>
                            </GridItem> */}
                                {errorMessages.length > 0 && (
                                    <GridItem {...styles.cartLineErrorMessageGridItem}>
                                        {errorMessages.map((message, index) => (
                                            <Typography
                                                {...styles.cartLineErrorMessageText}
                                                // eslint-disable-next-line react/no-array-index-key
                                                key={index}
                                                data-test-selector="cartline_errorMessage"
                                            >
                                                {message}
                                            </Typography>
                                        ))}
                                    </GridItem>
                                )}
                            </GridContainer>
                        </GridItem>
                        <GridItem {...styles.productPriceAndAvailabilityGridItem}>
                            {!cart.cartNotPriced && (
                                <>
                                    {showPriceMessage ? (
                                        <StyledWrapper>
                                            <Typography {...styles.notFoundLabel}>
                                                {translate("Your Price:")}
                                            </Typography>
                                        </StyledWrapper>
                                    ) : (
                                        <StyledWrapper>
                                            <Typography {...styles.notFoundLabel}>
                                                {translate("List Price:")}
                                            </Typography>
                                        </StyledWrapper>
                                    )}
                                    <ProductPrice
                                        product={cartLine}
                                        currencySymbol={cart.currencySymbol}
                                        showSavings={true}
                                        showSavingsAmount={productSettings.showSavingsAmount}
                                        showSavingsPercent={productSettings.showSavingsPercent}
                                        extendedStyles={styles.productPrice}
                                        showLabel={false}
                                        showUnitOfMeasure={showPerUnitPrice}
                                    />
                                </>
                            )}
                            {!showPriceMessage ? (
                                <StyledWrapper {...styles.itemNotFound}>
                                    <Link href="/contact-kgpco">{translate("Contact Us")}</Link>
                                    <Typography>{translate("for pricing & availability for this item.")}</Typography>
                                </StyledWrapper>
                            ) : null}
                            {showPriceMessage && (
                                <CartLineQuantity
                                    cart={cart}
                                    editable={true}
                                    onQtyOrderedChange={qtyOrderedChangeHandler}
                                    extendedStyles={styles.quantity}
                                    label="QTY"
                                />
                            )}
                            {minimumOrderQty > 1 && hasMinimumQtyError && (
                                <StyledWrapper {...styles.errMSg}>{siteMessage("min_qty_msg")}</StyledWrapper>
                            )}
                            {multipleSaleQty > 1 && hasMultipleSalesQtyError && (
                                <StyledWrapper {...styles.errMSg}>{siteMessage("multiple_qty_msg")}</StyledWrapper>
                            )}
                            {cartLine.pricing?.actualPrice === 0
                                ? null
                                : !cartLine.quoteRequired &&
                                  !cart.cartNotPriced && (
                                      <Typography
                                          {...styles.extendedUnitNetPriceText}
                                          data-test-selector="cartline_extendedUnitNetPrice"
                                      >
                                          {cartLine.pricing!.extendedUnitNetPriceDisplay}
                                      </Typography>
                                  )}
                            {showPricing &&
                                promotions.map(promotion => (
                                    <Typography {...styles.promotionNameText} key={promotion.id}>
                                        {promotion.name}
                                    </Typography>
                                ))}
                            {/* {showInventoryAvailability && !cartLine.quoteRequired && (
                            <ProductAvailability
                                productId={cartLine.productId!}
                                availability={cartLine.availability!}
                                unitOfMeasure={cartLine.unitOfMeasure}
                                trackInventory={cartLine.trackInventory}
                                failedToLoadInventory={cart.failedToGetRealTimeInventory}
                                extendedStyles={styles.productAvailability}
                            />
                        )} */}
                            <StyledWrapper {...styles.listRemoveCart}>
                                {showAddToList &&
                                    !hideAddToList &&
                                    cartLine.canAddToWishlist &&
                                    !cartLine.isRestricted &&
                                    showPriceMessage && (
                                        <Link {...styles.addToListLink} onClick={addToListClickHandler}>
                                            {translate("Add to List")}
                                        </Link>
                                    )}
                                {showRemoveAction && (
                                    <Clickable
                                        onClick={removeCartLineClickHandler}
                                        data-test-selector="cartline_removeLine"
                                        className={invalidProductsId?.includes(cartLine.id) ? "invalid" : "valid"}
                                        {...styles.removeItem}
                                    >
                                        {!showPriceMessage ? <Icon src={Trash} size={20} /> : null}

                                        {translate("Remove")}
                                    </Clickable>
                                )}
                            </StyledWrapper>
                        </GridItem>
                        {/* {showLineNotes && !cartLine.isPromotionItem && cart.properties["isPunchout"] === undefined && (
                        <GridItem {...styles.cartLineNotesGridItem}>
                            <CartLineNotes
                                cart={cart}
                                editable={true}
                                onNotesChange={notesChangeHandler}
                                extendedStyles={styles.cartLineNotes}
                            />
                        </GridItem>
                    )} */}
                    </GridContainer>
                </GridItem>
                {/* <GridItem {...styles.removeCartLineGridItem}>
                {showRemoveAction && (
                    <Clickable onClick={removeCartLineClickHandler} data-test-selector="cartline_removeLine">
                        <IconMemo {...styles.removeCartLineIcon} />
                    </Clickable>
                )}
            </GridItem> */}
                {showLineNotes && cart.properties["isPunchout"] === undefined && (
                    <StyledWrapper {...styles.cartLineNotesGridItem}>
                        <CartLineNotes
                            cart={cart}
                            editable={true}
                            onNotesChange={notesChangeHandler}
                            extendedStyles={styles.cartLineNotes}
                        />
                    </StyledWrapper>
                )}
            </GridContainer>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withCartLine(CartLineCardExpanded));
